import { Injectable } from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormGroup,
    isFormArray,
    UntypedFormArray,
    UntypedFormControl,
    UntypedFormGroup,
} from '@angular/forms';
import { FieldCondition, Trigger } from '@wdx/shared/utils';
import {
    Observable,
    debounceTime,
    filter,
    pairwise,
    startWith,
    takeUntil,
} from 'rxjs';
import {
    IFormControlData,
    IFormControlNameObject,
} from '../../models/reactive-dynamic.model';
import * as R from 'ramda';

@Injectable()
export class FormControlService {
    /**
     *  This has been created to split a string to find information like array name, index number and form control name
     *
     * @param {String} name This is the string that potential has the array name, index number and form control name
     * @return {IFormControlNameObject}
     */
    stringToFormControlObject(name: string): IFormControlNameObject {
        const NAME_ARR = name.split('.');
        const LEVEL_1 = NAME_ARR[0];
        const LEVEL_2 = NAME_ARR[1];
        const LEVEL_3 = NAME_ARR[2];

        const NAME_OBJECT: IFormControlNameObject = {
            Level1ControlName: LEVEL_1,
            Level1HasSubForm: false,
        };

        if (LEVEL_2) {
            NAME_OBJECT.Level2ControlName = LEVEL_2;
            NAME_OBJECT.Level1HasSubForm = true;
        }

        if (LEVEL_3) {
            NAME_OBJECT.Level3ControlName = LEVEL_3;
            NAME_OBJECT.Level2HasSubForm = true;
        }

        const regex = /\[\d+\]/gi;
        const subformPropNames = [
            {
                controlName: 'Level1ControlName',
                subformIndex: 'Level1SubFormIndex',
            },
            {
                controlName: 'Level2ControlName',
                subformIndex: 'Level2SubFormIndex',
            },
        ];
        NAME_ARR.slice(0, 2)?.forEach((part, i) => {
            const indexPart = part.match(regex)?.[0];

            if (indexPart) {
                (NAME_OBJECT as any)[subformPropNames[i].controlName] =
                    part.replace(indexPart, '');
                (NAME_OBJECT as any)[subformPropNames[i].subformIndex] =
                    parseInt(indexPart.replace('[', '').replace(']', ''), 10);
            }
        });

        return NAME_OBJECT;
    }

    /**
     *
     * @param {FormArray} formArray - The FormArray control from angular form controls
     * @param {number} formArrayIndex - This is the index that needs to be selected from the FormArray
     * @param {string} formControlName - This is the name of the control that needs to be returned
     * @returns {UntypedFormControl} - FormControl that is going to be returned
     */
    getFormArrayIndexFormControl(
        formArray: FormArray,
        formArrayIndex: number,
        formControlName: string,
    ): UntypedFormControl {
        return (formArray.controls.at(formArrayIndex) as FormGroup).get(
            formControlName as string,
        ) as UntypedFormControl;
    }

    /**
     *
     * @param {FormGroup} form This is the angular FormGroup
     * @param {string} name This is the name of the form control e.g 'firstName', 'arrayName.hair'
     * @param {number} indexes  This is the index of the items in the form array. This is optional
     *
     * @returns {IFormControlData[]} This returns an array of IFormControlData
     */
    getFormControlData(
        form: FormGroup,
        name: string,
        indexes?: number[],
    ): IFormControlData[] {
        const formControls: {
            index?: number;
            formControl: UntypedFormControl;
        }[] = [];
        const controlNameObject = this.stringToFormControlObject(name);
        const hasSubform = controlNameObject.Level1HasSubForm;
        const formControl = form?.get(controlNameObject.Level1ControlName);

        if (!formControl) {
            return [];
        }

        if (hasSubform) {
            if (isFormArray(formControl)) {
                const formArray = formControl as UntypedFormArray;

                const max = indexes?.length
                    ? indexes?.length
                    : formArray.controls?.length;
                let count = 0;

                while (count < max) {
                    const index = indexes?.length ? indexes[count] : count;

                    if (formArray.at(index)) {
                        formControls.push({
                            index: index,
                            formControl: this.getFormArrayIndexFormControl(
                                formArray,
                                index,
                                controlNameObject.Level2ControlName as string,
                            ),
                        });
                    }
                    count = count + 1;
                }
            } else {
                const formGroup = formControl as UntypedFormGroup;
                const childControl =
                    formGroup.controls[
                        controlNameObject.Level2ControlName as string
                    ];
                if (childControl) {
                    formControls.push({
                        formControl: childControl as UntypedFormControl,
                    });
                }
            }
        }

        if (!hasSubform) {
            if (formControls) {
                formControls.push({
                    formControl: formControl as UntypedFormControl,
                });
            }
        }
        return formControls;
    }

    /**
     *
     * @param {Trigger} trigger This is the trigger object from the form conditions
     * @param {IFormControlData} FORM_CONTROL_DATA This is an object of IFormControlData that as  form control witch will be watched for changes
     * @param {(trigger: Trigger, formControlData: IFormControlData)} fn This is a function that gets called when the form control emits a change. The 2 properties above are passed to the function
     * @param {Observable<boolean>} destroyed$ - This is observable to complete the subscription
     */
    setValueChangeListener(
        trigger: Trigger,
        FORM_CONTROL_DATA: IFormControlData,
        fn: (trigger: Trigger, formControlData: IFormControlData) => void,
        destroyed$: Observable<boolean>,
    ): void {
        FORM_CONTROL_DATA?.formControl?.valueChanges
            .pipe(
                takeUntil(destroyed$),
                startWith(FORM_CONTROL_DATA?.formControl?.value),
                pairwise(),
                filter(
                    ([prev, curr]) => prev !== curr && !R.equals(prev, curr),
                ),
                debounceTime(0),
            )
            .subscribe(() => {
                const currentSubFormIndex =
                    typeof FORM_CONTROL_DATA.index === 'number'
                        ? this.getSubformFormGroupFromChildControl(
                              FORM_CONTROL_DATA.formControl,
                          )
                        : undefined;
                fn(trigger, {
                    ...FORM_CONTROL_DATA,
                    index: currentSubFormIndex,
                });
            });
    }

    setFieldConditionValueChangeListener(
        conditions: FieldCondition[],
        inputFormControls: IFormControlData[],
        target: string,
        fn: (
            conditions: FieldCondition[],
            target: string,
            subformIndex?: number,
        ) => void,
        destroyed$: Observable<boolean>,
        subformIndex?: number,
    ): void {
        inputFormControls?.forEach(({ formControl }) =>
            formControl?.valueChanges
                .pipe(
                    takeUntil(destroyed$),
                    startWith(formControl?.value),
                    pairwise(),
                    filter(
                        ([prev, curr]) =>
                            prev !== curr && !R.equals(prev, curr),
                    ),
                    debounceTime(0),
                )
                .subscribe(() => {
                    // find the current subform index - the original index may no longer be correct as an
                    // earlier subform may have been deleted
                    const currentSubFormIndex =
                        typeof subformIndex === 'number'
                            ? this.getSubformFormGroupFromChildControl(
                                  formControl,
                              )
                            : undefined;
                    fn(conditions, target, currentSubFormIndex);
                }),
        );
    }

    getSubformFormGroupFromChildControl(
        formControl: AbstractControl,
    ): number | undefined {
        let parentFormGroup = formControl?.parent?.parent?.controls;
        let subformDirectChild = formControl;

        // handle 3rd levels i.e. address in a subform
        if (!Array.isArray(parentFormGroup)) {
            parentFormGroup = formControl?.parent?.parent?.parent?.controls;
            subformDirectChild = formControl.parent as AbstractControl;
        }
        if (!Array.isArray(parentFormGroup)) {
            return undefined;
        }
        return (parentFormGroup as FormGroup[])?.findIndex((x) =>
            Object.values(x.controls).some((y) => y === subformDirectChild),
        );
    }

    /**
     * Returns the path (plus other useful data) for the given form control
     * Does this by working backwards from the formcontrol to indentify all parents
     * and the names of the controls in the hierarchy
     * @param { AbstractControl } formControl
     */
    public getMetadataFromFormControl(formControl: AbstractControl): {
        controlHierarchy: AbstractControl[];
        nameHierarchy: (string | number | undefined)[];
        path?: string;
    } {
        let control = formControl;
        let controlHierarchy = [control];
        const _name = Object.entries(control?.parent?.controls ?? {}).find(
            ([, c]) => c === control,
        )?.[0];
        let nameHierarchy: (string | number | undefined)[] = [_name];
        while (control?.parent) {
            controlHierarchy = [control.parent, ...controlHierarchy];
            control = control?.parent;
            if (Array.isArray(control.parent?.controls)) {
                const index = control.parent.controls.findIndex(
                    (c) => c === control,
                );
                nameHierarchy = [index, ...nameHierarchy];
            } else if (control?.parent) {
                const name = Object.entries(
                    control?.parent?.controls ?? {},
                ).find(([, c]) => c === control)?.[0];
                nameHierarchy = [name, ...nameHierarchy];
            } else {
                nameHierarchy = ['root', ...nameHierarchy];
            }
        }
        const path =
            nameHierarchy.length > 1
                ? (nameHierarchy.slice(1).reduce((acc, curr, i, input) => {
                      const a = i > 0 ? '.' : '';
                      return (acc +=
                          typeof curr === 'number'
                              ? `[${curr}]`
                              : `${a}${curr}`);
                  }, '') as string)
                : nameHierarchy[0]?.toString();

        return { nameHierarchy, controlHierarchy, path };
    }

    /**
     * Apply a function to all descendants of a provided form control, for example
     * marking all as dirty as this not available in Angular v19 (addded in v20)
     * 
     * e.g.
      
        applyFuncToAllFormControlDescendants(control,
                    (formControl) => {
                        formControl?.markAsDirty();
                    },
                );
     * 
     * @param formControl starting form control
     * @param func function to apply to formcontrol
     * @param applyToSelf apply function to the initially provided form control
     * @returns void
     */
    public applyFuncToAllFormControlDescendants(
        formControl: AbstractControl,
        func: (formControl: AbstractControl) => void,
        applyToSelf = false,
    ) {
        if (applyToSelf) {
            func(formControl);
        }
        const controls = (formControl as FormArray)?.controls;
        if (!controls) {
            return;
        }

        if (Array.isArray(controls)) {
            controls.forEach((control) => {
                this.applyFuncToAllFormControlDescendants(control, func, true);
            });
            return;
        }

        Object.values(controls as Record<string, AbstractControl>)?.forEach(
            (control) =>
                this.applyFuncToAllFormControlDescendants(control, func, true),
        );
    }
}
